import { doc, getDoc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId } from '../tenantContext';

// The "Oil-Index" Data Structure
export interface MarketIndex {
    serviceType: string;
    region: string;
    lastUpdated: Date;

    // Hourly Rate Stats
    hourly: {
        count: number;
        mean: number;
        m2: number; // Sum of squares of differences from current mean (for variance/stdDev)
        min: number;
        max: number;
    };

    // Fixed Job Stats
    fixed: {
        count: number;
        mean: number;
        m2: number;
        min: number;
        max: number;
    };
}

/**
 * Z-Score Analysis Result
 * > 0: Above Average
 * < 0: Below Average
 * > 2.0: Statistical Outlier (Top 2.5% most expensive) - "Critical Alert"
 */
export interface PricingAnalysis {
    zScore: number;
    percentile: number;
    marketRate: number;
    marketStdDev: number;
    status: 'good' | 'fair' | 'high' | 'anomaly';
    confidence: number; // Based on sample size
}

const REGION = 'permian_basin'; // Hardcoded for now, would detect based on Well location

/**
 * 🧠 THE LEARNING ENGINE
 * Implements Welford's Online Algorithm to update Mean and Variance 
 * without reading all historical records. O(1) complexity.
 */
export async function learnFromTicket(
    serviceType: string,
    billingType: 'hourly' | 'fixed',
    cost: number,
    rate?: number
) {
    const typeKey = serviceType.toLowerCase().replace(/\s+/g, '_');
    const indexRef = doc(db, 'market_indices', `${REGION}_${typeKey}`);

    try {
        await runTransaction(db, async (transaction) => {
            const indexDoc = await transaction.get(indexRef);

            let data: MarketIndex;

            if (!indexDoc.exists()) {
                // Cold Start Initialization
                data = {
                    serviceType,
                    region: REGION,
                    lastUpdated: new Date(),
                    hourly: { count: 0, mean: 0, m2: 0, min: 99999, max: 0 },
                    fixed: { count: 0, mean: 0, m2: 0, min: 99999, max: 0 }
                };
            } else {
                data = indexDoc.data() as MarketIndex;
            }

            // Select the stats block to update
            const stats = billingType === 'hourly' ? data.hourly : data.fixed;
            const value = billingType === 'hourly' ? (rate || 0) : cost;

            if (value <= 0) return; // Skip invalid data

            // 1. Update Count
            const newCount = stats.count + 1;

            // 2. Update Mean (Rolling Average)
            const delta = value - stats.mean;
            const newMean = stats.mean + delta / newCount;

            // 3. Update M2 (for Variance)
            const delta2 = value - newMean;
            const newM2 = stats.m2 + delta * delta2;

            // 4. Update Min/Max
            const newMin = Math.min(stats.min, value);
            const newMax = Math.max(stats.max, value);

            // Apply updates to local object
            if (billingType === 'hourly') {
                data.hourly = { count: newCount, mean: newMean, m2: newM2, min: newMin, max: newMax };
            } else {
                data.fixed = { count: newCount, mean: newMean, m2: newM2, min: newMin, max: newMax };
            }
            data.lastUpdated = new Date();

            transaction.set(indexRef, data);
        });
    } catch (e) {
        console.error("Failed to train pricing model:", e);
    }
}

/**
 * ⚡ ACTIVE INTELLIGENCE QUERY
 * Returns real-time statistical analysis of the price.
 */
export async function analyzePriceLikelihood(
    serviceType: string,
    billingType: 'hourly' | 'fixed',
    value: number
): Promise<PricingAnalysis | null> {
    const typeKey = serviceType.toLowerCase().replace(/\s+/g, '_');
    const indexRef = doc(db, 'market_indices', `${REGION}_${typeKey}`);
    const snap = await getDoc(indexRef);

    if (!snap.exists()) return null; // No data yet

    const data = snap.data() as MarketIndex;
    const stats = billingType === 'hourly' ? data.hourly : data.fixed;

    if (stats.count < 5) return null; // Not enough data for statistical significance

    // Calculate Variance & StdDev
    const variance = stats.m2 / (stats.count - 1);
    const stdDev = Math.sqrt(variance);

    // Calculate Z-Score: (Value - Mean) / StdDev
    // Z-Score tells us how many standard deviations away from the mean this price is.
    const zScore = (value - stats.mean) / (stdDev || 1); // Avoid div by zero

    let status: PricingAnalysis['status'] = 'good';
    if (zScore > 1.0) status = 'fair';     // Top 16%
    if (zScore > 2.0) status = 'high';     // Top 2.5%
    if (zScore > 3.0) status = 'anomaly';  // Top 0.15% (Extreme outlier)

    return {
        zScore,
        percentile: calculatePercentile(zScore),
        marketRate: stats.mean,
        marketStdDev: stdDev,
        status,
        confidence: Math.min(stats.count / 20, 1.0) // 100% confidence at 20+ samples
    };
}

// Approximation of CDF for Normal Distribution
function calculatePercentile(z: number): number {
    // Sigmoid approx is close enough for UI feedback
    return Math.round((1 / (1 + Math.exp(-z * 1.6))) * 100);
}
